import type { AdminOrderResponse, AdminTerminationPreview } from '@inflip/contracts/admin';
import {
  OrderStatus,
  SettlementStatus,
  TERMINATION_KIND_LABELS,
  TERMINATION_REFUND_MODE_LABELS,
  TerminationKind,
  TerminationRefundMode,
  sellerShareOf,
  zonedDateOf,
} from '@inflip/shared';
import { useEffect, useId, useState } from 'react';

import { CheckboxIndicator } from './animated-checkbox';
import { REFUND_ACCOUNT_NOTE } from './confirm-deposit-dialog';
import { BankSelect, Button, Chip, Dialog, DialogFooter, inputCls } from './ui';
import { api } from '../lib/api-client';

const won = (n: number) => `${n.toLocaleString('ko-KR')}원`;
const dayLabel = (d: string) =>
  `${d.slice(0, 4)}년 ${Number(d.slice(5, 7))}월 ${Number(d.slice(8, 10))}일`;
const periodLabel = (p: string) => `${p.slice(0, 4)}년 ${Number(p.slice(5, 7))}월`;

/** 고른 선불 처리로 판매자 쪽 돈이 어떻게 되는지 — 서버(정산 생성·환불 완료 재계산·회수)와 같은 식 */
function sellerEffect(
  preview: AdminTerminationPreview,
  refund: number,
):
  | { kind: 'none' }
  | { kind: 'settle'; net: number }
  | { kind: 'cancel' }
  | { kind: 'clawback'; amount: number } {
  const remaining = preview.refundableAmount - refund;
  const s = preview.settlement;
  if (s?.status === SettlementStatus.PAID_OUT) {
    if (refund <= 0) return { kind: 'none' };
    const left = Math.max(0, s.netAmount - s.clawedBackAmount);
    const proRata = s.grossAmount > 0 ? Math.floor((s.netAmount * refund) / s.grossAmount) : 0;
    return { kind: 'clawback', amount: remaining <= 0 ? left : Math.min(left, proRata) };
  }
  const settles = (s && s.status !== SettlementStatus.CANCELLED) || preview.createsSettlement;
  if (!settles) return { kind: 'none' };
  if (remaining <= 0) return s ? { kind: 'cancel' } : { kind: 'none' };
  return { kind: 'settle', net: sellerShareOf(remaining, preview.platformFeeRateBp) };
}

/**
 * 계약 해지 — 사유, 선불 처리(비환불·부분 환불·전액 환불), 바뀌는 것 요약을 한 창에서.
 * 금액은 미리 계산해 보여 주고, 저장 때 서버가 다시 계산·검증한다.
 */
export function TerminateContractDialog({
  order,
  token,
  busy,
  error,
  onSubmit,
  onClose,
}: {
  order: AdminOrderResponse;
  token: string;
  busy: boolean;
  error: string | null;
  onSubmit(body: Parameters<typeof api.terminateOrder>[2]): void;
  onClose(): void;
}) {
  const ids = { reason: useId(), amount: useId() };
  const [preview, setPreview] = useState<AdminTerminationPreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [kind, setKind] = useState<TerminationKind>(TerminationKind.MUTUAL);
  const [reason, setReason] = useState('');
  const [mode, setMode] = useState<TerminationRefundMode>(TerminationRefundMode.NONE);
  const [amountText, setAmountText] = useState('');
  // 선불 환불 계좌 — 구매자가 등록한 환불 계좌로 미리 채운다
  const [bank, setBank] = useState(order.buyerRefundAccount?.bankName ?? '');
  const [accountNumber, setAccountNumber] = useState(order.buyerRefundAccount?.accountNumber ?? '');
  const [holder, setHolder] = useState(order.buyerRefundAccount?.holderName ?? '');

  useEffect(() => {
    api
      .terminationPreview(token, order.id)
      .then(setPreview)
      .catch((e: unknown) => setLoadError(e instanceof Error ? e.message : String(e)));
  }, [token, order.id]);

  const refundable = preview?.refundableAmount ?? 0;
  const partialAmount = Number(amountText || 0);
  const refund =
    mode === TerminationRefundMode.FULL
      ? refundable
      : mode === TerminationRefundMode.PARTIAL
        ? partialAmount
        : 0;
  const partialInvalid =
    mode === TerminationRefundMode.PARTIAL && (partialAmount <= 0 || partialAmount >= refundable);
  const needsAccount = !!preview?.needsBankAccount && refund > 0;
  const accountOk =
    !needsAccount || (!!bank.trim() && accountNumber.trim().length >= 4 && !!holder.trim());
  const canSubmit =
    !!preview &&
    !preview.blockedReason &&
    !busy &&
    reason.trim().length >= 5 &&
    !partialInvalid &&
    accountOk;

  const lastDay = preview?.royalty?.lastDay ?? zonedDateOf(new Date(), order.timeZone);
  const effect = preview ? sellerEffect(preview, refund) : null;

  const modeLine = (m: TerminationRefundMode): string => {
    if (!preview) return '';
    const r =
      m === TerminationRefundMode.FULL
        ? refundable
        : m === TerminationRefundMode.PARTIAL
          ? partialAmount
          : 0;
    const e = sellerEffect(preview, r);
    const buyer = r > 0 ? `구매자 환불 ${won(r)}` : '구매자 환불 없음';
    const seller =
      e.kind === 'settle'
        ? `판매자 정산 ${won(e.net)} (수수료 ${won(refundable - r - e.net)})`
        : e.kind === 'clawback'
          ? `판매자에게서 ${won(e.amount)} 회수 필요`
          : e.kind === 'cancel'
            ? '판매자 정산 취소'
            : preview.settlement?.status === SettlementStatus.PAID_OUT
              ? '판매자 정산은 이미 지급됨'
              : '판매자 정산 없음';
    return `${buyer} · ${seller}`;
  };

  return (
    <Dialog label="계약 해지" title="계약 해지" onClose={onClose} wide>
      <p className="text-sm text-muted-foreground">
        {order.listingTitle ?? '상품'} · 구매자 {order.buyerName ?? '—'} / 판매자{' '}
        {order.sellerName ?? '—'}
      </p>

      {loadError && (
        <p role="alert" className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          {loadError}
        </p>
      )}
      {!preview && !loadError && <p className="mt-4 text-sm text-muted-foreground">불러오는 중…</p>}

      {preview?.blockedReason && (
        <p className="mt-4 rounded-md bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
          {preview.blockedReason}
        </p>
      )}

      {preview && !preview.blockedReason && (
        <div className="mt-5 space-y-6">
          <section>
            <p className="text-sm font-bold">1. 해지 사유</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {Object.values(TerminationKind).map((k) => (
                <Chip key={k} active={kind === k} onClick={() => setKind(k)}>
                  {TERMINATION_KIND_LABELS[k]}
                </Chip>
              ))}
            </div>
            <label htmlFor={ids.reason} className="mt-2.5 block">
              <span className="sr-only">해지 사유</span>
              <textarea
                id={ids.reason}
                rows={3}
                maxLength={1000}
                className={`w-full resize-none py-2 ${inputCls}`}
                placeholder="예: 구매자 요청으로 사용 중단, 판매자 동의 (채팅 10/14)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </label>
            <p className="mt-1 text-xs text-muted-foreground">
              구매자·판매자에게 그대로 보여요 (5자 이상).
            </p>
          </section>

          <section>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-sm font-bold">2. 선불 처리</p>
              <p className="text-xs text-muted-foreground tabular-nums">
                선불 {won(preview.totalAmount)}
                {preview.refundedAmount > 0 && ` · 이미 환불 ${won(preview.refundedAmount)}`} ·
                돌려줄 수 있는 금액 {won(refundable)}
              </p>
            </div>
            {refundable <= 0 ? (
              <p className="mt-2 rounded-md bg-muted px-3 py-2.5 text-sm">
                돌려줄 선불이 없어 <b>{TERMINATION_REFUND_MODE_LABELS.NONE}</b>로 해지해요.
              </p>
            ) : (
              <div className="mt-2 space-y-1.5">
                {Object.values(TerminationRefundMode).map((m) => (
                  <div
                    key={m}
                    className={`rounded-md px-3 py-2.5 transition ${mode === m ? 'bg-brand-soft' : 'bg-muted/60 hover:bg-muted'}`}
                  >
                    <button
                      type="button"
                      role="radio"
                      aria-checked={mode === m}
                      className="flex w-full items-start gap-2.5 text-left"
                      onClick={() => setMode(m)}
                    >
                      <CheckboxIndicator checked={mode === m} size={18} />
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold">
                          해지 · {TERMINATION_REFUND_MODE_LABELS[m]}
                        </span>
                        <span className="block text-xs text-muted-foreground tabular-nums">
                          {m === TerminationRefundMode.PARTIAL && partialAmount <= 0
                            ? '돌려줄 금액을 입력하면 나눠진 금액이 보여요'
                            : modeLine(m)}
                        </span>
                      </span>
                    </button>
                    {m === TerminationRefundMode.PARTIAL && mode === m && (
                      <label htmlFor={ids.amount} className="mt-2 block pl-7">
                        <span className="text-xs font-semibold text-muted-foreground">
                          구매자에게 돌려줄 선불 (원)
                        </span>
                        <input
                          id={ids.amount}
                          inputMode="numeric"
                          className={`mt-1 w-full tabular-nums ${inputCls}`}
                          placeholder={`${won(refundable)}보다 적게`}
                          value={amountText ? Number(amountText).toLocaleString('ko-KR') : ''}
                          onChange={(e) => setAmountText(e.target.value.replace(/[^\d]/g, ''))}
                        />
                        {partialAmount >= refundable && (
                          <span className="mt-1 block text-xs font-semibold text-rose-600">
                            전부 돌려주려면 전액 환불(선불)을 골라 주세요.
                          </span>
                        )}
                      </label>
                    )}
                  </div>
                ))}
              </div>
            )}
            {needsAccount && (
              <div className="mt-2.5">
                <p className="text-xs font-semibold text-muted-foreground">
                  구매자 환불 계좌 (무통장)
                  {order.buyerRefundAccount && (
                    <span className="ml-1.5 font-normal">· {REFUND_ACCOUNT_NOTE}</span>
                  )}
                </p>
                <div className="mt-1 grid grid-cols-[minmax(0,1fr)_7rem] gap-1.5">
                  <BankSelect className="w-full min-w-0" value={bank} onChange={setBank} />
                  <input
                    className={`w-full min-w-0 ${inputCls}`}
                    placeholder="예금주"
                    value={holder}
                    onChange={(e) => setHolder(e.target.value)}
                  />
                  <input
                    className={`col-span-2 w-full min-w-0 ${inputCls}`}
                    placeholder="계좌번호"
                    inputMode="numeric"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                  />
                </div>
              </div>
            )}
            <p className="mt-2 text-xs text-muted-foreground">
              어느 방식이든 해지일까지 쓴 것은 인정되고 주문은 계약 해지로 끝나요. 계약을 처음부터
              무효로 하려면 해지 대신 [환불 직접 만들기]로 전액 환불해 주세요.
            </p>
          </section>

          <section>
            <p className="text-sm font-bold">3. 해지하면 바뀌는 것</p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
              <li>
                {preview.hasLicense
                  ? `라이선스: 지금 끝나요 (마지막 사용일 ${dayLabel(lastDay)})`
                  : '라이선스: 아직 발급 전이라 끝낼 라이선스가 없어요'}
              </li>
              {preview.royalty && (
                <li>
                  로열티: {periodLabel(preview.royalty.lastPeriod)}분(~
                  {dayLabel(preview.royalty.lastDay)})까지 보고받아요.
                  {preview.royalty.unreportedPeriods.length > 0
                    ? ` 아직 보고 안 된 달 ${preview.royalty.unreportedPeriods.length}개 (${preview.royalty.unreportedPeriods.map(periodLabel).join(', ')})`
                    : ' 남은 보고는 없어요.'}
                </li>
              )}
              {refund > 0 && (
                <li>
                  환불: {won(refund)}을 승인 상태로 만들어요. 송금한 뒤 환불 내역에서 [지급 완료
                  처리]를 눌러 주세요.
                </li>
              )}
              {effect?.kind === 'settle' && (
                <li>
                  정산: 판매 대금 {won(effect.net)}
                  {preview.createsSettlement && !preview.settlement
                    ? '을 새로 만들어요 (관리자 구매확정)'
                    : '으로 정산돼요'}
                  .{refund > 0 && ' 환불 지급이 끝나기 전에는 지급할 수 없어요.'}
                </li>
              )}
              {effect?.kind === 'cancel' && (
                <li>정산: 환불 지급이 끝나면 대기 중인 정산이 취소돼요.</li>
              )}
              {effect?.kind === 'clawback' && (
                <li className="font-semibold text-rose-600">
                  판매자에게 이미 지급한 {won(preview.settlement!.netAmount)} 중{' '}
                  {won(effect.amount)}을 따로 돌려받아야 해요. 자동 회수 기능은 아직 없어요.
                </li>
              )}
              {preview.fullyExclusive && <li>완전 독점이 풀려 다른 구매자가 살 수 있어요.</li>}
              {order.status === OrderStatus.DISPUTED && <li>분쟁은 계약 해지로 종결돼요.</li>}
              <li>구매자·판매자에게 알림과 채팅 메시지가 가요. 채팅방은 열려 있어요.</li>
            </ul>
          </section>
        </div>
      )}

      <DialogFooter
        notice={
          error && (
            <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          )
        }
      >
        <Button variant="secondary" className="flex-1" onClick={onClose}>
          닫기
        </Button>
        {preview && !preview.blockedReason && (
          <Button
            variant="danger"
            className="flex-1"
            disabled={!canSubmit}
            onClick={() =>
              onSubmit({
                kind,
                reason: reason.trim(),
                refundMode: refundable <= 0 ? TerminationRefundMode.NONE : mode,
                ...(mode === TerminationRefundMode.PARTIAL ? { refundAmount: partialAmount } : {}),
                ...(needsAccount
                  ? {
                      bankAccount: {
                        bank: bank.trim(),
                        accountNumber: accountNumber.trim(),
                        holder: holder.trim(),
                      },
                    }
                  : {}),
              })
            }
          >
            계약 해지하기
          </Button>
        )}
      </DialogFooter>
    </Dialog>
  );
}
